import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { sliceAtLatestCompaction } from 'js/src/agent/compaction';
import { SKILL_NAMES } from './skillNames';
import { getActiveSkills } from './skillStore';
import type { AnySkill, SkillSurface } from './types';
import { isAssistantMessage, isToolMessage, type ConversationMessage } from 'js/src/agent/types';

/**
 * Which skills the agent has loaded, derived from the conversation itself
 * rather than tracked as it goes. The conversation is the source of truth and
 * is rehydrated on resume, so a reloaded conversation still knows what it
 * loaded — module-level state would have been wiped by the reload.
 *
 * Used to mechanically enforce ordering rules that prose alone doesn't hold,
 * e.g. create_blank_video requiring a video skill to be loaded first.
 */
export function getLoadedSkillNames(messages: ConversationMessage[]): Set<string> {
    // A load_skill call only counts once its result comes back without an
    // error — an unknown skill name leaves the agent with no instructions.
    const failedCallIds = new Set(
        messages
            .filter(isToolMessage)
            .filter(
                (message) =>
                    message.isSuccess === false ||
                    (typeof message.content === 'string' && message.content.startsWith('Error')),
            )
            .map((message) => message.tool_call_id),
    );

    const loaded = new Set<string>();
    for (const message of messages) {
        if (!isAssistantMessage(message) || !message.tool_calls) {
            continue;
        }
        for (const call of message.tool_calls) {
            if (call.function.name !== TOOL_NAMES.loadSkill || failedCallIds.has(call.id)) {
                continue;
            }
            try {
                const name = JSON.parse(call.function.arguments)?.name;
                if (typeof name === 'string') {
                    loaded.add(name);
                }
            } catch {
                // Unparseable arguments never produced a loaded skill.
            }
        }
    }
    return loaded;
}

/**
 * The loaded skills whose instructions the model is still sent: a compaction stands in for every
 * message before it, so a skill loaded earlier has to be loaded again to be read.
 */
export function getSkillsInContext(messages: ConversationMessage[]): Set<string> {
    return getLoadedSkillNames(sliceAtLatestCompaction(messages).messages);
}

/** True when any currently-enabled video-kind skill has been loaded. */
export function hasLoadedVideoSkill(
    messages: ConversationMessage[],
    surface?: SkillSurface,
): boolean {
    const loaded = getLoadedSkillNames(messages);
    return getActiveSkills(surface).some(
        (skill) => skill.kind === 'video' && loaded.has(skill.name),
    );
}

/**
 * The recipe the video is being built under. `create-video` is the generic fallback, and a specific
 * recipe tells the agent to load it alongside, so the specific one wins whenever both are loaded.
 */
export function getActiveVideoRecipe(messages: ConversationMessage[]): AnySkill | undefined {
    const loaded = [...getLoadedSkillNames(messages)];
    const recipes = getActiveSkills().filter(
        (skill) => skill.kind === 'video' && loaded.includes(skill.name),
    );
    const latestFirst = (a: AnySkill, b: AnySkill) =>
        loaded.indexOf(b.name) - loaded.indexOf(a.name);
    return (
        recipes.filter(({ name }) => name !== SKILL_NAMES.createVideo).sort(latestFirst)[0] ??
        recipes[0]
    );
}

/** True when the surface offers at least one video-kind skill to load. */
export function hasAvailableVideoSkills(surface?: SkillSurface): boolean {
    return getActiveSkills(surface).some((skill) => skill.kind === 'video');
}
